import { useState, useMemo, useEffect, useRef } from 'react';
import { ValidatorForm } from 'react-form-validator-core';
import {
  DropdownList,
  TextField,
  TFVButton,
  Translate,
  TFVDateRangePicker,
} from '../../../Share/components';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import moment from 'moment';
import {
  createLicense,
  downgradeLicense,
  upgradeLicense,
  resetLicenseOperationSuccess,
} from '../../actions';
import { subscribe, unSubscribe } from '../../actions/eventBus';
import { generateUniqueId, createDefaultContactPerson } from '../../../Share/utils';
import ContactPersonEdit from '../ContactPersonEdit';

const CONTACT_PERSON_APPROVER_TYPE = 'ContactPersonApprover';

function LicenseForm({
  licenseTypes = [],
  contactPersons = [],
  onCreateLicense = () => {},
  onUpgradeLicense = () => {},
  onDowngradeLicense = () => {},
  callBack = () => {},
  onClose = () => {},
  formData = {},
  isEdit = false,
  upgrade = false,
  downgrade = false,
  displayConfirmation = false,
  licenseOperationSuccess = false,
  resetOperationSuccess = () => {},
}) {
  const [selectedLicense, setSelectedLicense] = useState(null);
  const [selectedNumberOfUnits, setSelectedNumberOfUnits] = useState(null);
  const [deliveryDate, setDeliveryDate] = useState({
    startDate: moment(),
    endDate: moment(),
    key: generateUniqueId(),
  });
  const [selectedContactPerson, setSelectedContactPerson] = useState(null);
  const [showContactPersonModal, setShowContactPersonModal] = useState(false);
  const [newContactPerson, setNewContactPerson] = useState(
    createDefaultContactPerson(),
  );
  const [
    pendingContactPersonIdentifier,
    setPendingContactPersonIdentifier,
  ] = useState(null);
  const isValidDropdownSelection = value =>
    !!value &&
    typeof value === 'object' &&
    value.value !== undefined &&
    value.value !== null &&
    value.value !== '';

  const licenseOptions = useMemo(() => {
    return licenseTypes.map(license => ({
      name: license.LicenseTypeName,
      value: license.LicenseTypeIdentifier,
    }));
  }, [licenseTypes]);

  const pricingOptions = useMemo(() => {
    if (!selectedLicense) return [];

    const license = licenseTypes.find(
      l => l.LicenseTypeIdentifier === selectedLicense.value,
    );

    if (!license) return [];

    const currentNumber = (formData && formData?.NumberOfLicenses) || 0;

    return license.Pricings.map(pricing => {
      let disabled = false;
      let currentLicense = pricing.NumberOfLicenses === currentNumber || false;
      if (upgrade) {
        disabled = pricing.NumberOfLicenses <= currentNumber;
      } else if (downgrade) {
        disabled = pricing.NumberOfLicenses >= currentNumber;
      }

      const option = {
        name: `${pricing.NumberOfLicenses} (${pricing.Price} SEK/månad)${disabled && currentLicense ? ' - Nuvarande' : ''}`,
        value: pricing.NumberOfLicenses,
      };

      if (upgrade || downgrade) {
        option.disabled = disabled;
      }

      return option;
    });
  }, [
    selectedLicense,
    licenseTypes,
    formData?.NumberOfLicenses,
    upgrade,
    downgrade,
  ]);

  const handleLicenseChange = event => {
    setSelectedLicense(event.target.value);
    setSelectedNumberOfUnits(null);
  };

  const handleNumberOfUnitsChange = event => {
    setSelectedNumberOfUnits(event.target.value);
  };

  const handleDateChange = (e, element) => {
    const { key } = deliveryDate;
    element.onToggleMenu();
    if (key && e[key]) {
      const { startDate, endDate } = e[key];
      if (startDate && endDate) {
        setDeliveryDate({
          startDate: moment(startDate),
          endDate: moment(endDate),
          key,
        });
      }
    }
  };

  const handleContactPersonChange = event => {
    setSelectedContactPerson(event.target.value);
  };

  const handleOpenContactPersonModal = () => {
    setNewContactPerson(createDefaultContactPerson());
    setShowContactPersonModal(true);
  };

  const handleCloseContactPersonModal = () => {
    setShowContactPersonModal(false);
  };

  const handleSubmit = () => {
    if (
      !isValidDropdownSelection(selectedNumberOfUnits) ||
      !isValidDropdownSelection(selectedContactPerson)
    ) {
      return;
    }

    if (upgrade) {
      onUpgradeLicense({
        licenseIdentifier: formData.LicenseIdentifier,
        numberOfLicenses: selectedNumberOfUnits.value,
        contactPerson: selectedContactPerson.value,
      });
    } else if (downgrade) {
      onDowngradeLicense({
        licenseIdentifier: formData.LicenseIdentifier,
        numberOfLicenses: selectedNumberOfUnits.value,
        contactPerson: selectedContactPerson.value,
      });
    } else {
      onCreateLicense({
        licenseTypeIdentifier: selectedLicense.value,
        numberOfLicenses: selectedNumberOfUnits.value,
        dateValidFrom: deliveryDate.startDate.format('YYYY-MM-DD'),
        contactPerson: selectedContactPerson.value,
        displayConfirmation: displayConfirmation,
      });
    }
  };

  const formRef = useRef(null);

  useEffect(() => {
    const handleContactPersonCreated = ({ ContactPersonIdentifier, type }) => {
      if (type === CONTACT_PERSON_APPROVER_TYPE) {
        setPendingContactPersonIdentifier(ContactPersonIdentifier);
      }
    };
    subscribe('SuccessfullyContactPersonCreate', handleContactPersonCreated);
    return () => {
      unSubscribe('SuccessfullyContactPersonCreate', handleContactPersonCreated);
    };
  }, []);

  useEffect(() => {
    if (pendingContactPersonIdentifier) {
      const match = contactPersons.find(
        person => person.value === pendingContactPersonIdentifier,
      );
      if (match) {
        setSelectedContactPerson(match);
        setPendingContactPersonIdentifier(null);
      }
    }
  }, [contactPersons, pendingContactPersonIdentifier]);

  useEffect(() => {
    if (licenseOperationSuccess) {
      setSelectedLicense(null);
      setSelectedNumberOfUnits(null);
      setDeliveryDate({
        startDate: moment(),
        endDate: moment(),
        key: 'selection',
      });
      setSelectedContactPerson(null);
      setTimeout(() => {
        if (formRef.current) {
          formRef.current.resetValidations();
        }
      }, 0);
      if (callBack) callBack();
      resetOperationSuccess();
    }
  }, [licenseOperationSuccess, callBack, resetOperationSuccess]);

  useEffect(() => {
    ValidatorForm.addValidationRule('validDropdownSelection', value =>
      isValidDropdownSelection(value),
    );
    if (formData) {
      const { LicenseTypeIdentifier, NumberOfLicenses, DateValidFrom } =
        formData;

      if (isEdit) {
        setSelectedLicense(
          licenseOptions.find(option => option.value === LicenseTypeIdentifier),
        );
        if (upgrade || downgrade) {
          setSelectedNumberOfUnits(null);
        } else {
          setSelectedNumberOfUnits(
            pricingOptions.find(option => option.value === NumberOfLicenses),
          );
        }
        const calculatedStartDate = downgrade
          ? formData?.DateFirstCancellationPossible
            ? moment(formData.DateFirstCancellationPossible).add(1, 'day')
            : moment(DateValidFrom || undefined)
          : upgrade
            ? moment()
            : moment(DateValidFrom);
        setDeliveryDate({
          startDate: calculatedStartDate,
          endDate: calculatedStartDate,
          key: 'selection',
        });
      }
    }
    ValidatorForm.addValidationRule('checkNumberOfUnits', value => {
      if (formData && value) {
        if (value.value === formData.NumberOfLicenses) {
          return false;
        }
      }
      return true;
    });
    return () => {
      ValidatorForm.removeValidationRule('checkNumberOfUnits');
      ValidatorForm.removeValidationRule('validDropdownSelection');
    };
  }, [formData, licenseOptions, pricingOptions, downgrade, upgrade]);

  return (
    <>
      <ValidatorForm ref={formRef} onSubmit={handleSubmit}>
        <div className="form-group">
          <label>
            <Translate content="aiTalk.license" />
          </label>
          <DropdownList
            name="license"
            className="ellipsis pl-3 py-1 tv-single-assign-form__control"
            options={licenseOptions}
            onChange={handleLicenseChange}
            value={selectedLicense}
            validators={['required']}
            errorMessages={[
              Translate({
                content: 'validator.isRequired',
                params: {
                  name: Translate({
                    content: 'aiTalk.license',
                  }),
                },
              }),
            ]}
            ariaLabel={Translate({
              content: 'aiTalk.license',
            })}
            disabled={!!isEdit}
          />
        </div>
        <div className="form-group">
          <div className="d-flex flex-row">
            <label htmlFor="numberOfUnits">
              <Translate content="aiTalk.numberOfUnits" />
            </label>
            <div className="tooltip-icon">
              <i className="fa fa-question-circle ml-2" aria-hidden="true" />
              <span className="tooltip-text">
                Antal enheter (mobil/surfplatta/dator) som det är möjligt att
                installera verktyget på.
              </span>
            </div>
          </div>

          <DropdownList
            name="numberOfUnits"
            className="ellipsis pl-3 py-1 tv-single-assign-form__control"
            options={pricingOptions}
            onChange={handleNumberOfUnitsChange}
            value={selectedNumberOfUnits}
            validators={[
              'required',
              'validDropdownSelection',
              'checkNumberOfUnits',
            ]}
            errorMessages={[
              Translate({
                content: 'validator.isRequired',
                params: {
                  name: Translate({
                    content: 'aiTalk.numberOfUnits',
                  }),
                },
              }),
              Translate({
                content: 'validator.isRequired',
                params: {
                  name: Translate({
                    content: 'aiTalk.numberOfUnits',
                  }),
                },
              }),
              'Samma värde är valt',
            ]}
            ariaLabel={Translate({
              content: 'aiTalk.numberOfUnits',
            })}
            disabled={!selectedLicense}
          />
        </div>
        <div className="form-group">
          <label>
            <Translate content="aiTalk.startDate" />
          </label>
          <div
            className={`tv-single-assign-form__input ${!!isEdit ? 'disabled-date-range' : ''}`}
          >
            <TFVDateRangePicker
              ranges={deliveryDate}
              onChange={handleDateChange}
              ariaLabel={Translate({
                content: 'aiTalk.startDate',
              })}
              displayFormat="YYYY-MM-DD"
              disabled={!!isEdit}
              enableDefaultLicense
            />
          </div>
        </div>
        <div className="form-group">
          <label htmlFor="contactPersonIdentifierApproved">
            <Translate content="aiTalk.contactPersonIdentifierApproved" />
          </label>
          <DropdownList
            name="contactPersonIdentifierApproved"
            className="ellipsis pl-3 py-1 tv-single-assign-form__control"
            options={contactPersons}
            onChange={handleContactPersonChange}
            value={selectedContactPerson}
            validators={['required', 'validDropdownSelection']}
            errorMessages={[
              Translate({
                content: 'validator.isRequired',
                params: {
                  name: Translate({
                    content: 'aiTalk.contactPersonIdentifierApproved',
                  }),
                },
              }),
              Translate({
                content: 'validator.isRequired',
                params: {
                  name: Translate({
                    content: 'aiTalk.contactPersonIdentifierApproved',
                  }),
                },
              }),
            ]}
            footer={
              <span
                onClick={e => {
                  e.stopPropagation();
                  handleOpenContactPersonModal();
                }}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    e.stopPropagation();
                    handleOpenContactPersonModal();
                  }
                }}
                className="tv-dropdown__footer-conactperson-add tv-dropdown__footer-conactperson-add-ordercontactPerson"
                tabIndex={0}
                role="button"
                aria-label="Lägg till beställare"
              >
                + Lägg till beställare
              </span>
            }
            ariaLabel={Translate({
              content: 'aiTalk.contactPersonIdentifierApproved',
            })}
          />
        </div>
        <div className="justify-content-between tv-display-flex gap-4 tv-create-license__modal__footer">
          <TFVButton
            type="line-primary"
            buttonType="button"
            className="w-50"
            text={Translate({
              content: 'aiTalk.close',
            })}
            onClick={() => {
              setSelectedLicense(null);
              setSelectedNumberOfUnits(null);
              setDeliveryDate({
                startDate: moment(),
                endDate: moment(),
                key: 'selection',
              });
              setSelectedContactPerson(null);
              onClose();
            }}
          />
          <TFVButton
            type="secondary"
            buttonType="submit"
            className="w-50"
            text={
              isEdit
                ? Translate({
                    content: 'aiTalk.updateLicense',
                  })
                : Translate({
                    content: 'aiTalk.signLicense',
                  })
            }
          />
        </div>
      </ValidatorForm>
      {showContactPersonModal && (
        <ContactPersonEdit
          {...newContactPerson}
          type={CONTACT_PERSON_APPROVER_TYPE}
          showModal={showContactPersonModal}
          inOrderProcess
          onHandleModal={handleCloseContactPersonModal}
        />
      )}
    </>
  );
}

LicenseForm.propTypes = {
  licenseTypes: PropTypes.arrayOf(PropTypes.shape({})),
  contactPersons: PropTypes.arrayOf(PropTypes.shape({})),
  onCreateLicense: PropTypes.func.isRequired,
  onClose: PropTypes.func,
  formData: PropTypes.shape({}),
  callBack: PropTypes.func,
  isEdit: PropTypes.bool,
  upgrade: PropTypes.bool,
  downgrade: PropTypes.bool,
  onUpgradeLicense: PropTypes.func,
  onDowngradeLicense: PropTypes.func,
  displayConfirmation: PropTypes.bool,
  licenseOperationSuccess: PropTypes.bool,
  resetOperationSuccess: PropTypes.func,
};

const mapStateToProps = state => ({
  licenseTypes: state.user.licenseTypes,
  contactPersons: state.user.contactPersons || [],
  licenseOperationSuccess: state.user.licenseOperationSuccess,
});
const mapDispatchToProps = (dispatch, ownProps) => ({
  onCreateLicense: ({
    licenseTypeIdentifier,
    numberOfLicenses,
    dateValidFrom,
    contactPerson,
    displayConfirmation,
  }) => {
    dispatch(
      createLicense(
        licenseTypeIdentifier,
        numberOfLicenses,
        dateValidFrom,
        contactPerson,
        displayConfirmation,
      ),
    );
  },
  onUpgradeLicense: ({
    licenseIdentifier,
    numberOfLicenses,
    contactPerson,
  }) => {
    dispatch(
      upgradeLicense(licenseIdentifier, numberOfLicenses, contactPerson),
    );
  },
  onDowngradeLicense: ({
    licenseIdentifier,
    numberOfLicenses,
    contactPerson,
  }) => {
    dispatch(
      downgradeLicense(licenseIdentifier, numberOfLicenses, contactPerson),
    );
  },
  resetOperationSuccess: () => {
    dispatch(resetLicenseOperationSuccess());
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(LicenseForm);
